import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/lib/motion";
import { IMAGES, VALUES } from "@/data/content";

const chapters = [
  { n: "01", title: "Our Mission", text: "To help individuals live healthier, less stressful and more balanced lives through accessible, professional wellness services — brought directly to them." },
  { n: "02", title: "Our Vision", text: "To become one of the UK's most trusted mobile wellness providers, starting in Manchester and expanding thoughtfully across the country." },
  { n: "03", title: "Our Promise", text: "A full health consultation on your first visit, a professional couch, fresh linens and premium oils — every treatment tailored to you." },
];

const About = () => (
  <div data-testid="about-page">
    <PageHero eyebrow="About Risi Wellness" title="Wellness, brought to you." image={IMAGES.manifesto}
      subtitle="A professional wellness company dedicated to improving physical and mental wellbeing through high-quality mobile services." />

    {/* Story */}
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl">
          <img src={IMAGES.twoMassage} alt="Risi Wellness treatment" className="w-full h-[520px] object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold mb-4">Our Story</p>
        <h2 className="font-serif text-3xl sm:text-5xl text-primary leading-tight mb-6">Comfort, convenience & exceptional care.</h2>
        <p className="text-muted-foreground text-lg leading-relaxed mb-4">
          Risi Wellness Limited was founded on a simple belief: that professional, restorative wellness should fit
          around your life — not the other way around. We bring treatments directly to your home, hotel or workplace,
          so you can enjoy comfort and care without travelling.
        </p>
        <p className="text-muted-foreground text-lg leading-relaxed">
          Initially serving Manchester and the surrounding areas, we're building a reputation for professionalism,
          warmth and genuine results — with plans to expand throughout the UK.
        </p>
      </Reveal>
    </section>

    {/* Manifesto chapters */}
    <section className="bg-card border-y border-border">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 space-y-20">
        {chapters.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-border pt-10">
              <span className="md:col-span-2 font-serif text-6xl sm:text-8xl text-secondary/60 leading-none">{c.n}</span>
              <h3 className="md:col-span-4 font-serif text-3xl sm:text-4xl text-primary">{c.title}</h3>
              <p className="md:col-span-6 text-muted-foreground text-lg leading-relaxed">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* Values */}
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
      <Reveal>
        <p className="text-xs tracking-[0.3em] uppercase text-secondary font-semibold mb-3">Our Values</p>
        <h2 className="font-serif text-4xl sm:text-6xl text-primary leading-none mb-12">What guides us</h2>
      </Reveal>
      <div className="flex flex-wrap gap-4">
        {VALUES.map((v, i) => (
          <Reveal key={v} delay={i * 0.05}>
            <span className="inline-block rounded-full border border-primary/25 px-7 py-3 text-lg font-serif text-primary hover:bg-primary hover:text-primary-foreground transition-colors">{v}</span>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <Link to="/booking" className="inline-flex items-center gap-2 mt-14 rounded-full bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-primary/90 transition-colors">
          Book a Treatment <ArrowUpRight size={18} />
        </Link>
      </Reveal>
    </section>
  </div>
);

export default About;
